import { debounce } from 'lodash-es';
import mitt from 'mitt';

import { uuid } from '../uuid';

const eventBus = mitt();

export const emitter = {
  _listeners: {} as {
    [x: string]: ((data?: any) => void)[];
  },

  emit(event: string, data?: any) {
    eventBus.emit(event, data);
  },

  on(event: string, callback: (data?: any) => void) {
    const listenersByEvent = this._listeners[event] || [];

    eventBus.on(event, callback);
    this._listeners[event] = listenersByEvent.concat(callback);
  },

  once(event: string, callback: (data?: any) => void) {
    const _onceCallback = (data?: any) => {
      callback(data);

      this.off(event, _onceCallback);
      delete this._listeners[event];
    };

    this.on(event, _onceCallback);
  },

  off(event: string, callback: (data?: any) => void) {
    const listenersByEvent = this._listeners[event] || [];

    eventBus.off(event, callback);
    this._listeners[event] = listenersByEvent.filter(cb => cb !== callback);
  },

  listeners(event: string) {
    return this._listeners[event] || [];
  },
};

export class CustomEvent<T extends string, K = T, Target = any> {
  readonly trigger: K;
  readonly type: T;
  readonly timestamp: number;

  private _defaultPrevented = false;
  private _target: Target;

  constructor(type: T, trigger?: K, target?: Target) {
    this.type = type;
    this.trigger = trigger!;
    this._target = target ?? ({} as Target);
    this.timestamp = Date.now();
  }

  get defaultPrevented() {
    return this._defaultPrevented;
  }

  get target() {
    return this._target;
  }

  get preventDefault() {
    return () => {
      this._defaultPrevented = true;
    };
  }
}

export const createCustomEvent = <T extends string, TG, TR = T>(
  type: T,
  target: TG,
  trigger?: TR,
) => new CustomEvent(type, trigger ?? type, target);

export class CustomEventEmitter<
  T extends { [eventName: string]: CustomEvent<any, any, any> },
> {
  private _uuid = uuid();
  private _listenerMap = {} as {
    [N in keyof T & string]: ((event: T[N]) => any)[];
  };

  constructor() {}

  disposeEvent() {
    for (const key in this._listenerMap) {
      const listenerList = this._listenerMap[key];

      listenerList.forEach(cb => this.off(key as any, cb));
    }
  }

  emit<N extends keyof T & string>(
    eventName: N,
    payload?: T[N]['target'],
    option?: {
      trigger?: T[N]['trigger'];
      debounceTime?: number;
    },
  ) {
    const name = `${this._uuid}-${eventName}`;
    const trigger = option?.trigger;

    if (option?.debounceTime != null) {
      debounce(emitter.emit, option.debounceTime).bind(emitter)(
        name,
        createCustomEvent(name, payload, trigger),
      );

      return;
    }

    emitter.emit(name, createCustomEvent(name, payload, trigger));
  }

  off<N extends keyof T & string>(
    eventName: N,
    callback?: (event: T[N]) => any,
  ) {
    if (!this._listenerMap[eventName]?.length) return;

    const name = `${this._uuid}-${eventName}`;
    const targetCallbackList = callback
      ? [callback]
      : this._listenerMap[eventName];

    targetCallbackList.forEach(callback => {
      emitter.off(name, callback);

      this._listenerMap[eventName] = this._listenerMap[eventName].filter(
        cb => cb !== callback,
      );
    });
  }

  on<N extends keyof T & string>(eventName: N, callback: (event: T[N]) => any) {
    const name = `${this._uuid}-${eventName}`;

    this._listenerMap[eventName] ??= [];
    this._listenerMap[eventName].push(callback);

    emitter.on(name, callback);
  }
}
